import { enqueueSnackbar } from 'notistack';
import { ERROR_401 } from 'common/config/api';
import { SESSION_EXPIRED_MESSAGE, SESSION_EXPIRED_MESSAGE_TIMEOUT, SIGN_IN_VIEW } from 'common/constants/auth';
import {
  buildSignInUrl,
  singleFlightTokenRefresh,
} from 'pages/Auth/utils';
import BaseService from './base';

type AuthErrorLike = {
  // Only the top-level `.status` shape is needed here — axios-shaped errors are not used in this service layer.
  status?: number;
};

const isAuthError = (error: unknown): error is AuthErrorLike =>
  typeof error === 'object' && error !== null && 'status' in error;

// HMAC portal 401s mean the request link is invalid or expired, not that a JWT
// session died. Treating them as auth expiry sends anonymous company users to login.
const isHmacPortalRequest = (url: string): boolean =>
  /\/api\/(?:documents|questionnaires)\/requests\/(?:validate-token\/|file-upload-status\/|\d+\/)/.test(
    url
  );

const handleExpiredSession = () => {
  enqueueSnackbar(SESSION_EXPIRED_MESSAGE, {
    variant: 'info',
  });
  // Prevent any further API requests
  setTimeout(() => {
    window.location.href = buildSignInUrl({
      returnTo: `${window.location.pathname}${window.location.search}`,
      view: SIGN_IN_VIEW.IDENTIFIER,
    });
  }, SESSION_EXPIRED_MESSAGE_TIMEOUT);
};

const refreshPassThrough = async <TResponse>(
  url: string,
  next: () => Promise<TResponse>
): Promise<TResponse> => {
  try {
    return await next();
  } catch (error) {
    if (isAuthError(error) && error.status === Number(ERROR_401) && !isHmacPortalRequest(url)) {
      const result = await singleFlightTokenRefresh();

      if (result.kind === 'success') {
        return next();
      }

      if (result.kind === 'cleared') {
        handleExpiredSession();
      }
      // 'transient': rethrow the original 401 without triggering logout
    }
    throw error;
  }
};

class BaseServiceRetryWrapper extends BaseService {
  async get<TResponse = unknown>(url: string, headers?: Record<string, string>): Promise<TResponse> {
    return refreshPassThrough(url, () => super.get<TResponse>(url, headers));
  }

  async getFile<TResponse = unknown, TPayload = unknown>(
    url: string,
    data: TPayload,
    headers?: Record<string, string>
  ): Promise<TResponse> {
    return refreshPassThrough(url, () => super.getFile<TResponse, TPayload>(url, data, headers));
  }

  async downloadFile<TResponse = Blob>(url: string, headers?: Record<string, string>): Promise<TResponse> {
    return refreshPassThrough(url, () => super.downloadFile<TResponse>(url, headers));
  }

  async put<TResponse = unknown, TPayload = unknown>(
    url: string,
    data: TPayload,
    headers?: Record<string, string>
  ): Promise<TResponse> {
    return refreshPassThrough(url, () => super.put<TResponse, TPayload>(url, data, headers));
  }

  async post<TResponse = unknown, TPayload = unknown>(
    url: string,
    data?: TPayload,
    headers?: Record<string, string>
  ): Promise<TResponse> {
    return refreshPassThrough(url, () => super.post<TResponse, TPayload>(url, data, headers));
  }

  async delete<TResponse = unknown>(url: string, headers?: Record<string, string>): Promise<TResponse> {
    return refreshPassThrough(url, () => super.delete<TResponse>(url, headers));
  }

  async patch<TResponse = unknown, TPayload = unknown>(
    url: string,
    data: TPayload,
    headers?: Record<string, string>
  ): Promise<TResponse> {
    return refreshPassThrough(url, () => super.patch<TResponse, TPayload>(url, data, headers));
  }
}

export default BaseServiceRetryWrapper;
